import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {sendDeepResearchMessage} from 'llm/deep-research/DeepResearchDAO';
import {DeepResearchEmptyState} from 'llm/deep-research/DeepResearchEmptyState';
import {
  DeepResearchMessageComponent,
  DeepResearchMessage,
  DeepResearchMessageType,
} from 'llm/deep-research/DeepResearchMessage';
export interface DeepResearchMessageThreadControls {
  messages: Array<DeepResearchMessage>;
  chatId: string | undefined;
  isSubmitting: boolean;
  sendMessage: (messageContent: string) => Promise<void>;
}

export function useDeepResearchMessageThread(): DeepResearchMessageThreadControls {
  const [messages, setMessages] = useState<Array<DeepResearchMessage>>([]);
  const [chatId, _] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const updateProgress = useCallback(noop, []);

  const addSystemReport = useCallback((content: string) => {
    const systemMessage: DeepResearchMessage = {
      type: DeepResearchMessageType.SYSTEM_REPORT,
      content,
      timestamp: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, systemMessage]);
  }, []);

  const sendMessage = useCallback(
    async (messageContent: string) => {
      const trimmedMessage = messageContent.trim();

      const userMessage: DeepResearchMessage = {
        type: DeepResearchMessageType.USER_MESSAGE,
        content: trimmedMessage,
        timestamp: new Date(),
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setIsSubmitting(true);

      // TODO (BNCH-126624): Try catch and handle error

      const result = await sendDeepResearchMessage(chatId, trimmedMessage, updateProgress);
      addSystemReport(result.response);

      setMessages((prevMessages) => [...prevMessages]);
    },
    [chatId, updateProgress, addSystemReport]
  );

  return {
    messages,
    chatId,
    isSubmitting,
    sendMessage,
  };
}

export interface DeepResearchMessageThreadProps {
  messages: Array<DeepResearchMessage>;
}

export function DeepResearchMessageThread({messages}: DeepResearchMessageThreadProps): JSX.Element {
  if (messages.length === 0) {
    return <DeepResearchEmptyState />;
  }

  return (
    <div className={css(styles.threadContainer)}>
      {messages.map((message, index) => (
        <DeepResearchMessageComponent key={index} message={message} />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    padding: '16px',
    minHeight: 0,
  },
});

export default DeepResearchMessageThread;
